import { useQuery } from '@tanstack/react-query'
import { handoffContextService } from 'v2/shared/services/handoff-context-service'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import type { HandoffCaller, RCallHandoffPreview } from 'v2/types/api/handoff-context'
import { type CallDetails, CallDirection } from 'v2/types/call'
import dayjs from 'v2/utils/dayjs'

/** Presentation only: never replace callWith, the contact record, or callback targets. */
export const callHandoffPresentation = (
  call: CallDetails | null,
  data: RCallHandoffPreview | undefined,
  isError: boolean,
) => {
  if (
    !call ||
    call.direction !== CallDirection.INBOUND ||
    isError ||
    data?.status !== 'available'
  ) {
    return null
  }
  const handoff = data.handoff
  if (!handoff) return null
  const number = handoff.original_caller
  return {
    number: typeof number === 'string' && /^\+[1-9][0-9]{1,14}$/.test(number) ? number : null,
    source: handoff.source,
    via: handoff.via,
    reason: handoff.transfer_reason,
  }
}

/** A handoff caller is who the source reported, never the AI's own line or "Anonymous". */
export const handoffCallerName = (number?: string | null, source?: string | null) =>
  number || `Caller from ${source || 'external AI'}`

/** Set by HS only on handoff conversations; ordinary conversations never read handoff context. */
export const handoffCaller = (conversation?: object) =>
  (conversation as { handoff_caller?: HandoffCaller | null } | undefined)?.handoff_caller

export const useCallHandoffPreview = (
  call: CallDetails | null,
  isCallActive: boolean,
  hsEnabled: boolean,
) => {
  // Only invites for calls with external AI context carry the conversation; ordinary calls never poll.
  const conversation = call?.handoffConversation || ''
  const enabled =
    hsEnabled &&
    !!conversation &&
    call?.direction === CallDirection.INBOUND &&
    call.meta !== 'Monitored Call'
  const query = useQuery({
    queryKey: conversationQueryKeys().handoffCallPreview(conversation),
    queryFn: async () => (await handoffContextService.getCallPreview(conversation)).data,
    enabled,
    retry: false,
    gcTime: 0,
    staleTime: 0,
    refetchOnWindowFocus: true,
    // Ringing agents do not receive conversation events yet. Poll through initial offer/read
    // races and late uploads. After pickup, events plus a pending grace window refresh context.
    refetchInterval: (query) => {
      if (!enabled) return false
      if (!isCallActive) return 3000
      const data = query.state.data
      const pendingSince = data?.pending_since
        ? dayjs(data.pending_since).valueOf()
        : dayjs(call.startTime).valueOf()
      return (!data || data.status === 'pending') && dayjs().valueOf() - pendingSince < 45_000
        ? 3000
        : false
    },
  })
  return enabled ? callHandoffPresentation(call, query.data, query.isError) : null
}
